import { cn } from "@/lib/utils";
import { useState, useEffect, useCallback } from "react";

type ProductIconProps = {
  className?: string;
  baseCode: string;
};

// Global cache to track which images have been successfully loaded
const loadedImages = new Set<string>();

// Track failed URLs (expire after 5 minutes to allow retry)
const failedUrls = new Map<string, number>();
const EXPIRY_MS = 5 * 60 * 1000;

const isFailed = (url: string): boolean => {
  const failedAt = failedUrls.get(url);
  if (!failedAt) return false;
  if (Date.now() - failedAt > EXPIRY_MS) {
    failedUrls.delete(url);
    return false;
  }
  return true;
};

/**
 * ProductIcon component that displays token icons
 * 
 * Simple fallback chain (no external dependencies):
 * 1. Local SVG (/icons/tokens/{symbol}.svg) - crisp scaling for web
 * 2. Local PNG (/icons/tokens/{symbol}.png) - fallback for missing SVGs
 * 3. 2-letter text fallback - last resort
 * 
 * Admins upload icons when creating markets. No runtime external fetches.
 */
export const ProductIcon = ({ className, baseCode }: ProductIconProps) => {
  const symbol = baseCode.toLowerCase();
  
  // Simple URL chain: SVG first, then PNG
  const svgUrl = `/icons/tokens/${symbol}.svg`;
  const pngUrl = `/icons/tokens/${symbol}.png`;
  
  // Determine initial URL
  const getInitialUrl = (): string | null => {
    if (loadedImages.has(svgUrl)) return svgUrl;
    if (loadedImages.has(pngUrl)) return pngUrl;
    if (!isFailed(svgUrl)) return svgUrl;
    if (!isFailed(pngUrl)) return pngUrl;
    return null;
  };

  const [imgUrl, setImgUrl] = useState<string | null>(getInitialUrl);
  const [isLoaded, setIsLoaded] = useState(() => 
    imgUrl ? loadedImages.has(imgUrl) : false
  );

  // Reset when baseCode changes
  useEffect(() => {
    const url = getInitialUrl();
    setImgUrl(url);
    setIsLoaded(url ? loadedImages.has(url) : false);
  }, [symbol]);

  const handleLoad = useCallback(() => {
    if (imgUrl) {
      loadedImages.add(imgUrl);
      setIsLoaded(true);
    }
  }, [imgUrl]);

  const handleError = useCallback(() => {
    if (!imgUrl) return;
    
    // Mark current URL as failed
    failedUrls.set(imgUrl, Date.now());
    
    // Try PNG if SVG failed
    if (imgUrl === svgUrl && !isFailed(pngUrl)) {
      setImgUrl(pngUrl);
      setIsLoaded(false);
    } else {
      // All options exhausted, show text fallback
      setImgUrl(null);
    }
  }, [imgUrl, svgUrl, pngUrl]);

  const showFallback = imgUrl === null;

  return (
    <div
      className={cn(
        "relative flex size-5 shrink-0 overflow-hidden rounded-full",
        className
      )}
    >
      {/* Text fallback */}
      {showFallback && (
        <div className="flex size-full items-center justify-center text-[10px] font-medium text-muted-foreground bg-muted rounded-full">
          {baseCode.substring(0, 2).toUpperCase()}
        </div>
      )}
      
      {/* Image */}
      {!showFallback && imgUrl && (
        <img
          key={imgUrl}
          src={imgUrl}
          alt={baseCode}
          className={cn(
            "aspect-square size-full object-cover transition-opacity duration-150",
            isLoaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};
